// src/components/ui/accordion.js
import React, { useRef, useState } from "react";

/**
 * Animated Accordion (smooth open + close)
 * Props:
 * - items: [{ title: string, content: ReactNode }]
 */
const Accordion = ({ items = [] }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const contentRefs = useRef([]);

  // keep refs array length in sync
  if (contentRefs.current.length !== items.length) {
    contentRefs.current = Array(items.length)
      .fill()
      .map((_, i) => contentRefs.current[i] || React.createRef());
  }

  const toggle = (index) => {
    const willOpen = index !== activeIndex;
    const prevIndex = activeIndex;

    // close previously open panel (if any and different)
    if (prevIndex !== null && prevIndex !== index) {
      collapsePanel(contentRefs.current[prevIndex]);
    }

    if (willOpen) {
      expandPanel(contentRefs.current[index]);
      setActiveIndex(index);
    } else {
      // clicking the open one -> close it
      collapsePanel(contentRefs.current[index]);
      setActiveIndex(null);
    }
  };

  const expandPanel = (el) => {
    if (!el) return;
    // ensure element is visible for measuring
    el.style.display = "block";

    // get the height to animate to
    const targetHeight = el.scrollHeight;

    // start from 0
    el.style.height = "0px";
    // force reflow
    el.getBoundingClientRect();

    // apply transition and grow to target
    el.style.transition = "height 400ms ease";
    el.style.height = `${targetHeight}px`;

    // on end -> clear inline height so it becomes "auto" and stays responsive
    const onEnd = (ev) => {
      // ensure this event is the height transition
      if (ev.propertyName !== "height") return;
      el.style.transition = "";
      el.style.height = ""; // remove fixed height -> behaves like auto
      el.removeEventListener("transitionend", onEnd);
    };
    el.addEventListener("transitionend", onEnd);
  };

  const collapsePanel = (el) => {
    if (!el) return;

    // if current height is auto, compute its pixel height first
    const currentHeight = el.scrollHeight;

    // set explicit height then force reflow
    el.style.height = `${currentHeight}px`;
    el.getBoundingClientRect();

    // then animate to 0
    el.style.transition = "height 400ms ease";
    requestAnimationFrame(() => {
      el.style.height = "0px";
    });

    const onEnd = (ev) => {
      if (ev.propertyName !== "height") return;
      // hide and cleanup
      el.style.transition = "";
      // el.style.display = "none";
      el.style.height = "0px";
      el.removeEventListener("transitionend", onEnd);
    };
    el.addEventListener("transitionend", onEnd);
  };

  const renderedItems = items.map((item, index) => {
    const isActive = index === activeIndex;
    return (
      <div className="accordion-item" key={index}>
        <h2 className="accordion-header">
          <button
            className={`accordion-button ${isActive ? "" : "collapsed"}`}
            type="button"
            onClick={() => toggle(index)}
            aria-expanded={isActive ? "true" : "false"}
            aria-controls={`accordion-body-${index}`}
            id={`accordion-heading-${index}`}
          >
            {item.title}
          </button>
        </h2>

        <div
          id={`accordion-body-${index}`}
          role="region"
          aria-labelledby={`accordion-heading-${index}`}
          className={`accordion-collapse collapse ${isActive ? "" : ""}`}
          // store actual DOM element ref (not React.createRef object)
          ref={(el) => (contentRefs.current[index] = el)}
          // initial styles: collapsed panels hidden; animation is driven by expand/collapse functions
          style={{
            // display: isActive ? "block" : "none",
            overflow: "hidden",
            // DO NOT set height: 'auto' here animation needs numeric heights handled in functions
            height: isActive ? "105px" : "0px",
          }}
        >
          <div className="accordion-body">{item.content}</div>
        </div>
      </div>
    );
  });

  return <div className="accordion" id="accordionExample">{renderedItems}</div>;
};

export default Accordion;
